import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { pushDepositPaid } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DepositPanel = ({ requestId = null }) => {
  const [state, setState] = useState("idle"); // idle | creating | waiting | paid | error
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCheckout = async () => {
    setState("creating");
    try {
      const { data } = await axios.post(`${API}/deposits/checkout`, { request_id: requestId });
      window.open(data.checkout_url, "_blank", "noopener");
      setState("waiting");
      timerRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/deposits/${data.order_id}`);
          if (res.data.status === "PAID") {
            pushDepositPaid(data.order_id);
            setState("paid");
            clearInterval(timerRef.current);
          }
        } catch {
          /* keep polling */
        }
      }, 4000);
    } catch {
      setState("error");
    }
  };

  return (
    <div
      data-testid="deposit-panel"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[var(--brand-gold-soft)] bg-[var(--brand-cream)] px-4 py-2.5 sm:px-6"
    >
      {state === "paid" ? (
        <p data-testid="deposit-paid-message" className="flex items-center gap-2 text-sm font-medium text-emerald-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          Deposit received — thank you! Your spot is secured.
        </p>
      ) : state === "error" ? (
        <p data-testid="deposit-error" className="text-sm text-[var(--brand-muted)]">
          Online deposit is temporarily unavailable — you can pay the $25 deposit when the office
          confirms your visit.
        </p>
      ) : (
        <>
          <p className="flex items-center gap-2 text-xs sm:text-sm text-[var(--brand-muted)] min-w-0">
            <ShieldCheck className="h-4 w-4 shrink-0 text-[var(--brand-gold)]" aria-hidden="true" />
            <span>
              <span className="font-semibold text-[var(--brand-navy)]">Optional $25 deposit</span>{" "}
              — applied toward your visit.
            </span>
          </p>
          {state === "waiting" ? (
            <p data-testid="deposit-waiting" className="flex items-center gap-2 text-sm text-[var(--brand-navy)]">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Complete payment in the new tab — we&rsquo;ll confirm it here.
            </p>
          ) : (
            <button
              data-testid="deposit-pay-button"
              onClick={startCheckout}
              disabled={state === "creating"}
              className="inline-flex items-center gap-2 bg-[var(--brand-navy)] px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[var(--brand-navy-deep)] disabled:opacity-60"
            >
              {state === "creating" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Pay $25 deposit securely
            </button>
          )}
        </>
      )}
    </div>
  );
};
